import { useEffect, useState } from "react";
import { FiBell, FiBookOpen, FiCheck, FiClock, FiTag, FiTruck } from "react-icons/fi";
import useNotificationPreferences from "api/notificationResource/useNotificationPreferences";
import useUpdateNotificationPreferences from "api/notificationResource/useUpdateNotificationPreferences";
import useGenres from "api/GenreResource/useGenres";
import useProfile from "api/UserResource/useProfile";
import AccountLayout, { AccountSkeleton } from "../AccountLayout";
import AccountPage from "../AccountPage";

const options = [
	{
		key: "price_drop_alerts",
		label: "Wishlist price drops",
		description: "A heads-up when a book you’ve saved goes on sale.",
		icon: FiTag,
	},
	{
		key: "new_book_alerts",
		label: "New books you’ll love",
		description: "New arrivals in the genres you choose below.",
		icon: FiBookOpen,
	},
	{
		key: "reading_reminders",
		label: "A nudge to read",
		description: "Gentle reminders to pick up where you left off.",
		icon: FiClock,
	},
	{
		key: "order_status_updates",
		label: "Delivery updates",
		description: "Keep track of your orders from dispatch to delivery.",
		icon: FiTruck,
	},
];

const ToggleRow = ({ option, checked, disabled, onChange }) => {
	const Icon = option.icon;
	return (
		<div className="account-notification-row">
			<div className="account-notification-info">
				<div className="account-notification-icon">
					<Icon aria-hidden="true" />
				</div>
				<div>
					<h3 id={`${option.key}-label`}>{option.label}</h3>
					<p id={`${option.key}-description`}>{option.description}</p>
				</div>
			</div>
			<button
				type="button"
				role="switch"
				className="account-switch"
				aria-labelledby={`${option.key}-label`}
				aria-describedby={`${option.key}-description`}
				aria-checked={checked}
				disabled={disabled}
				onClick={() => onChange(!checked)}
			>
				<span />
			</button>
		</div>
	);
};

const NotificationPreferences = () => {
	const { data: profile, isLoading } = useProfile();
	const { data: prefs, isLoading: loadingPrefs, isError, refetch } = useNotificationPreferences();
	const {
		data: genresRes,
		isLoading: loadingGenres,
		isError: genresError,
		refetch: reloadGenres,
	} = useGenres("yes");
	const { mutateAsync: updatePrefs, isLoading: updating } = useUpdateNotificationPreferences();
	const [settings, setSettings] = useState({});
	const [status, setStatus] = useState("");
	useEffect(() => {
		if (prefs) setSettings(prefs);
	}, [prefs]);
	const genres = genresRes?.data?.data ?? [];
	const selectedGenres = Array.isArray(settings.alert_genres) ? settings.alert_genres : [];
	const save = async (change) => {
		const previous = settings;
		setSettings({ ...settings, ...change });
		setStatus("Saving your preferences…");
		try {
			await updatePrefs(change);
			setStatus("Preferences saved");
		} catch (_) {
			setSettings(previous);
			setStatus("Couldn’t save. Your previous preferences were restored.");
		}
	};
	return (
		<AccountPage>
			<AccountLayout isLoading={isLoading} profile={profile?.data}>
				{loadingPrefs ? (
					<AccountSkeleton />
				) : isError ? (
					<div className="account-empty" role="alert">
						<FiBell aria-hidden="true" />
						<h2>Preferences couldn’t be loaded</h2>
						<p>Please try again before changing your notifications.</p>
						<button type="button" className="account-button" onClick={() => refetch()}>
							Try again
						</button>
					</div>
				) : (
					<>
						<div className="account-section-heading">
							<h2>Email notifications</h2>
							<p>A little less noise, a little more of what matters.</p>
						</div>
						{options.map((option) => (
							<ToggleRow
								key={option.key}
								option={option}
								checked={settings[option.key] ?? true}
								disabled={updating}
								onChange={(value) => save({ [option.key]: value })}
							/>
						))}
						{(settings.new_book_alerts ?? true) && (
							<section className="account-genre-section">
								<div className="account-section-heading">
									<h2>Your favourite genres</h2>
									<p>Pick the genres you’d like new-book alerts for.</p>
								</div>
								<div className="account-genre-options">
									{genres.map((genre) => {
										const selected = selectedGenres.includes(genre.id);
										return (
											<button
												type="button"
												className="account-genre-chip"
												key={genre.id}
												aria-pressed={selected}
												disabled={updating}
												onClick={() =>
													save({
														alert_genres: selected
															? selectedGenres.filter((id) => id !== genre.id)
															: [...selectedGenres, genre.id],
													})
												}
											>
												{selected && <FiCheck aria-hidden="true" />}
												{genre.name}
											</button>
										);
									})}
								</div>
								{loadingGenres ? (
									<p className="account-helper" role="status">
										Loading genres…
									</p>
								) : genresError ? (
									<button
										type="button"
										className="account-inline-link"
										onClick={() => reloadGenres()}
									>
										Couldn’t load genres. Try again
									</button>
								) : (
									genres.length === 0 && (
										<p className="account-helper">No genres are available yet.</p>
									)
								)}
							</section>
						)}
						<div className="account-form-actions">
							<p className="account-save-status" role="status" aria-live="polite">
								<FiCheck aria-hidden="true" />
								{status || "Changes save automatically"}
							</p>
						</div>
					</>
				)}
			</AccountLayout>
		</AccountPage>
	);
};
export default NotificationPreferences;
